/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import memoize from 'memoize-one';
import moment from 'moment';
import urlJoin from 'proper-url-join';

import {
  Action,
  AndIntentCondition,
  DATA_TYPE,
  DateResponse,
  GenericResponse,
  IntentCondition,
  OptionResponse,
  ResolverType,
  ResponseCollectionBehavior,
  Step,
  TurnEventType
} from '../../../ts/action';
import { Entity } from '../../../ts/state';
import { yesNoTranslation } from './sysTranslations.js';
import {
  CUSTOM_CATEGORY,
  DATA_TYPE_RESPONSE_TYPES as DATA_TYPE_RESPONSE_TYPES_IMPORT,
  ENTITY_TYPES as ENTITY_TYPES_IMPORT,
  TIME_VARS as TIME_VARS_IMPORT,
  VARIABLE_CATEGORIES,
  generateComplexVariableId,
  getVariableIdFromVariableValue,
  isSubactionVariable
} from './variableUtil';

const nonAlphaNumericRegex = new RegExp('[^a-zA-Z\\d_-]', 'g');
export const variableNamesRegex = /\${(.*?)}/g;

export const NOT_FOUND_HANDLER = 'validation_not_found_handler';
export const NOT_FOUND_MAX_TRIES_HANDLER =
  'validation_not_found_max_tries_handler';

export const TIME_VARS = TIME_VARS_IMPORT; //in time, these re-exports should be refactored entirely to reference variableUtil

export const DASH_FORMAT = 'YYYY-MM-DD';
export const SLASH_FORMAT = 'MM/DD/YYYY';
export const STANDARD_FORMAT = 'hh:mm A';
export const MILITARY_FORMAT = 'HH:mm';
export const AM = 'AM';
export const PM = 'PM';

export const DATA_TYPE_RESPONSE_TYPES = DATA_TYPE_RESPONSE_TYPES_IMPORT;

export const YES_NO_TYPE_VALUES = {
  NO: 'no',
  YES: 'yes'
};

export const TEXT_AREA_INPUT = 'text-area-input';

export const ENTITY_TYPES = ENTITY_TYPES_IMPORT; // in the future we should clean this up to have all references point directly to variableUtil

export const RESPONSE_TYPES = {
  text: 'text',
  option: 'option',
  PAUSE: 'pause',
  IMAGE: 'image',
  search_skill: 'search_skill',
  channel_transfer: 'channel_transfer',
  connect_to_agent: 'connect_to_agent',
  date: 'date',
  AUDIO: 'audio',
  VIDEO: 'video',
  IFRAME: 'iframe',
  RECORD: 'record',
  response_from_data_type: 'response_from_data_type',
  response_from_variable: 'response_from_variable',
  suggestion: 'suggestion',
  search: 'search',
  conversational_search: 'conversational_search'
};

export const MEDIA_RESPONSES = [
  RESPONSE_TYPES.AUDIO,
  RESPONSE_TYPES.IFRAME,
  RESPONSE_TYPES.IMAGE,
  RESPONSE_TYPES.VIDEO
];

export const SYSTEM_VARIABLE_TYPES = {
  current_date: ENTITY_TYPES.date.id,
  current_time: ENTITY_TYPES.time.id,
  now: ENTITY_TYPES.date_time.id
};

export const ENTITIES_REQUIRE_NUMBER = ['sys-currency', 'sys-percentage'];

export const RESOLVER_PROPERTIES = {
  CALLOUT: 'callout',
  CLIENT_ACTIONS: 'client_actions',
  INVOKE_ACTION: 'invoke_action'
};

const RESOLVERS_WITH_SETTINGS = [
  ResolverType.CALL_AN_EXTENSION,
  ResolverType.CLIENT_ACTIONS,
  ResolverType.CONNECT_TO_AGENT,
  ResolverType.INVOKE_ANOTHER_ACTION,
  ResolverType.JUMP_TO,
  ResolverType.REPLAY,
  ResolverType.SEARCH
];

const RESOLVERS_WITH_OPTIONAL_SETTINGS = [ResolverType.SEARCH];

export const RESOLVER_POLICY_DEFAULT = 'default';
export const RESOLVER_POLICY_DYNAMIC = 'dynamic';

export const DYNAMIC_POLICY_LABEL = 'Let assistant decide';
export const DYNAMIC_POLICY_VALUE = 'let_assistant_decide';
//DO NOT DELETE the following, although "no action matches" is no longer the title of the action
//it is still used as the literal value of the scalar conditions, and as a result must be checked for
export const NO_ACTION_MATCHES_LITERAL = 'No action matches';
export const NO_MATCHES_LABEL = 'No matches';

export const AUDIO_WAV = 'audio/wav';

export const RECORDING_TERMINATION_KEYS = [
  {
    id: '0',
    text: '0'
  },
  {
    id: '1',
    text: '1'
  },
  {
    id: '2',
    text: '2'
  },
  {
    id: '3',
    text: '3'
  },
  {
    id: '4',
    text: '4'
  },
  {
    id: '5',
    text: '5'
  },
  {
    id: '6',
    text: '6'
  },
  {
    id: '7',
    text: '7'
  },
  {
    id: '8',
    text: '8'
  },
  {
    id: '9',
    text: '9'
  },
  {
    id: '#',
    text: '#'
  },
  {
    id: '*',
    text: '*'
  }
];

// For Phone audio attestation, TTS can not pronounce # and * so need to provide text in place of those keys
export const RECORDING_TERMINATION_SPECIAL_KEYS_TTS_CALLOUT_MAP = {
  '#': 'pound',
  '*': 'star'
};

export const RECORDING_PROMPT_INDICATOR = {
  BEEP: 'Beep',
  SILENT: 'Silent (pause)'
};

export const errorResponseStrings = {
  ar: {
    ERROR_RESPONSE: 'عفوا، لم أستوعب هذا، يرجى اعادة صياغة ردك. ',
    NUMBER_ERROR_RESPONSE: 'لم أستوعب هذا. أدخل رقم. ',
    OPTIONS_ERROR_RESPONSE: 'لم أستوعب هذا. حدد اختيار صحيح: ',
    INVALID_RESPONSE:
      'أخشى أنني لا أفهم. دعني أرى ما اذا كان بامكاني المساعدة بطريقة أخرى. '
  },
  'pt-br': {
    ERROR_RESPONSE: 'Eu não entendi, reformule sua resposta.',
    NUMBER_ERROR_RESPONSE: 'Eu não entendi. Insira um número.',
    OPTIONS_ERROR_RESPONSE: 'Eu não entendi. Selecione uma opção válida:',
    INVALID_RESPONSE:
      'Acho que não entendi. Vamos ver se posso ajudar de outro jeito.'
  },
  cs: {
    ERROR_RESPONSE:
      'Omlouvám se, to jsem nezachytil, prosím, zopakujte svou odpověď.',
    NUMBER_ERROR_RESPONSE: 'To jsem nezachytil. Zadejte číslo.',
    OPTIONS_ERROR_RESPONSE: 'To jsem nezachytil. Vyberte platnou možnost:',
    INVALID_RESPONSE:
      'Obávám se, že tomu nerozumím. Dejte vědět, jestli mohu pomoci jinak.'
  },
  de: {
    ERROR_RESPONSE:
      'Das habe ich leider nicht verstanden. Bitte formulieren Sie Ihre Antwort anders.',
    NUMBER_ERROR_RESPONSE:
      'Das habe ich leider nicht verstanden. Geben Sie eine Zahl ein.',
    OPTIONS_ERROR_RESPONSE:
      'Das habe ich leider nicht verstanden. Wählen Sie eine gültige Option aus:',
    INVALID_RESPONSE:
      'Das habe ich leider nicht verstanden. Vielleicht kann ich Ihnen auf andere Weise helfen.'
  },
  en: {
    ERROR_RESPONSE:
      "I'm sorry, I did not catch that, please restate your response.",
    NUMBER_ERROR_RESPONSE: "I didn't catch that. Enter a number.",
    OPTIONS_ERROR_RESPONSE: "I didn't catch that. Select a valid option:",
    INVALID_RESPONSE:
      "I'm afraid I don't understand. Let me see if I can help another way."
  },
  es: {
    ERROR_RESPONSE:
      'Lo siento pero no lo entiendo, por favor, vuelva a formular su respuesta. ',
    NUMBER_ERROR_RESPONSE: 'No lo he entendido. Especifique un número. ',
    OPTIONS_ERROR_RESPONSE:
      'No lo he entendido. Seleccione una opción válida: ',
    INVALID_RESPONSE:
      'Lo siento pero no lo entiendo. Déjeme ver si le puedo ayudar de otra manera. '
  },
  fr: {
    ERROR_RESPONSE:
      "Désolé, je n'ai pas compris, veuillez reformuler votre réponse.",
    NUMBER_ERROR_RESPONSE: "Je n'ai pas compris. Entrez un nombre.",
    OPTIONS_ERROR_RESPONSE:
      "Je n'ai pas compris. Sélectionnez une option valide :",
    INVALID_RESPONSE:
      "Je crains de ne pas comprendre. Peut-être puis-je vous aider d'une autre façon."
  },
  it: {
    ERROR_RESPONSE:
      'Mi dispiace, non ho  capito, per favore ripetere la risposta.',
    NUMBER_ERROR_RESPONSE: 'Non ho capito. Immettere un numero.',
    OPTIONS_ERROR_RESPONSE: "Non ho capito. Selezionare un'opzione valida:",
    INVALID_RESPONSE:
      'Temo di non capire. Mi faccia controllare se posso aiutare in altro modo.'
  },
  ja: {
    ERROR_RESPONSE:
      '申し訳ございません。分かりませんでした。回答を言い換えていただけますか。',
    NUMBER_ERROR_RESPONSE: '分かりませんでした。番号を入力してください。',
    OPTIONS_ERROR_RESPONSE:
      '分かりませんでした。有効なオプションを選択してください:',
    INVALID_RESPONSE:
      '申し訳ございません。分かりませんでした。別の方法でお手伝いできるか考えてみます。'
  },
  ko: {
    ERROR_RESPONSE:
      '죄송합니다. 잘 이해하지 못했습니다. 답변을 다시 입력해 주시기 바랍니다.',
    NUMBER_ERROR_RESPONSE:
      '잘 이해하지 못했습니다. 숫자를 입력해 주시기 바랍니다.',
    OPTIONS_ERROR_RESPONSE:
      '잘 이해하지 못했습니다. 올바른 옵션을 선택하시기 바랍니다.',
    INVALID_RESPONSE:
      '죄송하지만 이해하기 어렵습니다. 도와드릴 수 있는 다른 방법이 있는지 알아보겠습니다.'
  },
  nl: {
    ERROR_RESPONSE:
      'Sorry, ik heb het niet goed begrepen, kunt u dat anders zeggen.',
    NUMBER_ERROR_RESPONSE: 'Ik heb het niet begrepen. Geef een getal op.',
    OPTIONS_ERROR_RESPONSE:
      'Ik heb het niet begrepen. Selecteer een geldige optie:',
    INVALID_RESPONSE:
      'Ik ben bang dat ik het niet begrijp. Ik kijk of ik op een andere manier kan helpen. '
  },
  'zh-cn': {
    ERROR_RESPONSE: '抱歉，我没听清楚，请重述您的响应。',
    NUMBER_ERROR_RESPONSE: '我没听清楚。请输入数字。',
    OPTIONS_ERROR_RESPONSE: '我没听清楚。请选择有效选项：',
    INVALID_RESPONSE: '恐怕我不明白。让我看看是否可以通过其他方式提供帮助。'
  },
  'zh-tw': {
    ERROR_RESPONSE: '很抱歉，我沒有聽清楚，請重新說明您的回應。',
    NUMBER_ERROR_RESPONSE: '我沒有聽清楚。請輸入數字。',
    OPTIONS_ERROR_RESPONSE: '我沒有聽清楚。請選取有效的選項：',
    INVALID_RESPONSE: '我想我不明白。讓我看看是否可以提供其他協助。'
  },
  xx: {
    ERROR_RESPONSE:
      "I'm sorry, I did not catch that, please restate your response.",
    NUMBER_ERROR_RESPONSE: "I didn't catch that. Enter a number.",
    OPTIONS_ERROR_RESPONSE: "I didn't catch that. Select a valid option:",
    INVALID_RESPONSE:
      "I'm afraid I don't understand. Let me see if I can help another way."
  }
};

export const strings = {
  ERROR_RESPONSE:
    "I'm sorry, I did not catch that, please restate your response.",
  NUMBER_ERROR_RESPONSE: "I didn't catch that. Enter a number.",
  OPTIONS_ERROR_RESPONSE: "I didn't catch that. Select a valid option:"
};

export const DEFAULT_ACTIONS = {
  WELCOME: 'welcome',
  ANYTHING_ELSE: 'anything_else',
  FALLBACK: 'fallback',
  RUN_ALWAYS: 'run_always'
};

export const DAYS_OF_THE_WEEK = [
  {
    scalar: 7,
    text: 'Sunday'
  },
  {
    scalar: 1,
    text: 'Monday'
  },
  {
    scalar: 2,
    text: 'Tuesday'
  },
  {
    scalar: 3,
    text: 'Wednesday'
  },
  {
    scalar: 4,
    text: 'Thursday'
  },
  {
    scalar: 5,
    text: 'Friday'
  },
  {
    scalar: 6,
    text: 'Saturday'
  }
];

export const algorithmVersions = {
  versionOne: 'v1',
  versionTwo: 'v2'
};

export const algorithmModelVersions = {
  beta: 'beta',
  baseline: 'baseline',
  latest: 'latest',
  previous: 'previous'
};

/**
 * Get the data_type of an entity.
 * **Return `entity` as it if the entity is a custom entity**
 * @param {string} entityId the entity id
 * @param {boolean} keepEntityId to set if we return 'any' for other entity or the original entity ID. Default is true.
 *
 * @returns {string} the corresponding data type.
 */
export const getDataTypeByEntity = (entityId, keepEntityId = true) => {
  switch (entityId) {
    case ENTITY_TYPES.currency.id:
      return DATA_TYPE.CURRENCY;
    case ENTITY_TYPES.date.id:
      return DATA_TYPE.DATE;
    case ENTITY_TYPES.date_time.id:
      return DATA_TYPE.DATETIME;
    case ENTITY_TYPES.time.id:
      return DATA_TYPE.TIME;
    case ENTITY_TYPES.free_text.id:
      return DATA_TYPE.STRING;
    case ENTITY_TYPES.number.id:
      return DATA_TYPE.NUMBER;
    case ENTITY_TYPES.percent.id:
      return DATA_TYPE.PERCENTAGE;
    case ENTITY_TYPES.confirmation.id:
      return DATA_TYPE.YES_NO;
    default:
      return keepEntityId ? entityId : DATA_TYPE.ANY;
  }
};

/**
 * Get the data type of a system variable. It can be a custom entity id.
 * @param {string} systemVariableId the system_variable value
 * @returns {string} the data type it should be
 */
export const getDataTypeBySystemVariable = systemVariableId => {
  switch (systemVariableId) {
    case 'now':
      return DATA_TYPE.DATETIME;
    case 'current_time':
      return DATA_TYPE.TIME;
    case 'current_date':
      return DATA_TYPE.DATE;
    case 'fallback_reason':
      // it's not a data type, but a system custom entity.
      return 'sys-synthetic_fallback_reason_entity';
    case 'channel_name':
      //all the channels will be this type
      return 'sys-synthetic_channel_name_entity';
    case 'last_seen_at':
      return DATA_TYPE.STRING;
    case 'no_action_matches_count':
      return DATA_TYPE.INT;
    case 'timezone': // This could be string type in the future
    case 'locale': // This could be string type in the future
    default:
      return DATA_TYPE.ANY;
  }
};

export const isDateValue = value => moment(value, DASH_FORMAT).isValid();

export const isTimeValue = value => moment(value, MILITARY_FORMAT).isValid();

export const isNumericScalar = scalar => typeof scalar === DATA_TYPE.NUMBER;

export const isNumericDataType = type => {
  switch (type) {
    case DATA_TYPE.CURRENCY:
    case DATA_TYPE.INT:
    case DATA_TYPE.INTEGER:
    case DATA_TYPE.NUMBER:
    case DATA_TYPE.FLOAT:
    case DATA_TYPE.DOUBLE:
    case DATA_TYPE.DECIMAL:
    case DATA_TYPE.PERCENTAGE:
      return true;
    default:
      return false;
  }
};

export const isNonUniqueNumericDataType = type => {
  switch (type) {
    case DATA_TYPE.INT:
    case DATA_TYPE.INTEGER:
    case DATA_TYPE.NUMBER:
    case DATA_TYPE.FLOAT:
    case DATA_TYPE.DOUBLE:
      return true;
    default:
      return false;
  }
};

export const isScalarDataType = type => {
  switch (type) {
    case DATA_TYPE.BOOLEAN:
    case DATA_TYPE.STRING:
      return true;
    default:
      return isNumericDataType(type);
  }
};

export const isDatetimeType = type =>
  type === DATA_TYPE.DATE ||
  type === DATA_TYPE.TIME ||
  type === DATA_TYPE.DATETIME;

export const isDefaultAction = actionId => {
  return Object.values(DEFAULT_ACTIONS).includes(actionId);
};

// TO-DO: need help with this
export const isTour = _actionId => {
  return false;
};

export const isResolverWithSettings = resolverType => {
  return RESOLVERS_WITH_SETTINGS.includes(resolverType);
};

export const isResolverWithRequiredSettings = resolverType => {
  const resolversWithRequiredSettings = _.difference(
    RESOLVERS_WITH_SETTINGS,
    RESOLVERS_WITH_OPTIONAL_SETTINGS
  );
  return resolversWithRequiredSettings.includes(resolverType);
};

export const buildOptionsList = optionsArray =>
  optionsArray.map(option => {
    return {
      label: option.value,
      value: {
        input: {
          text: option.value
        }
      }
    };
  });

export const generateDataTypeId = () => {
  return `data-type_${Math.round(Math.random() * (50000 - 1000) + 1000)}`;
};

export const generateEntityName = () => {
  let id = `entity_${Math.round(Math.random() * (50000 - 1000) + 1000)}`;
  id = id.replace(nonAlphaNumericRegex, '_');
  if (id.length > 64) {
    id = id.substring(0, 63);
  }
  return id;
};

export const generateActionIntentName = actionId => {
  let id = `${actionId}_intent_${Math.round(
    Math.random() * (50000 - 1000) + 1000
  )}`;
  id = id.replace(nonAlphaNumericRegex, '_');
  if (id.length > 64) {
    id = id.substring(0, 63);
  }
  return id;
};

export const getActionName = (action, examples = {}) => {
  let name = 'Untitled action';
  if (action && typeof action.title === 'string' && action.title.length > 0) {
    name = action.title;
  } else if (action?.newAction && Object.keys(examples).length > 0) {
    name = examples[Object.keys(examples)[0]].text.substring(0, 64);
  }
  return name;
};

export const getActionIntentId = (action: Action): string | null =>
  action.condition &&
  ((action.condition as IntentCondition).intent ||
    (action.condition as AndIntentCondition).and?.[0]?.intent);

export const getDescriptionValue = action => {
  return _.get(action, 'description');
};

export const isResponseTooComplex = step => {
  const outputGeneric = _.get(step, 'output.generic');
  const textIndex = _.findIndex(outputGeneric, { response_type: 'text' });
  if (!outputGeneric || textIndex === -1) {
    return false;
  }
  const concat = _.get(
    step,
    `output.generic[${textIndex}].values[0].text_expression.concat`,
    null
  );
  if (!Array.isArray(concat)) {
    return false;
  }
  if (concat.length > 30) {
    return true;
  }
  return outputGeneric.length > 20;
};

export const textExpressionPiecesToText = concat => {
  let text = '';
  if (Array.isArray(concat)) {
    concat.forEach(item => {
      if (item.scalar) {
        text += item.scalar;
      }
      if (item.variable !== undefined) {
        if (item.variable_path !== undefined) {
          // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
          text += `\$\{${generateComplexVariableId(
            item.variable,
            item.variable_path
            // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
          )}\}`;
        } else if (item.child_property !== undefined) {
          // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
          text += `\$\{${generateComplexVariableId(
            item.variable,
            item.child_property
            // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
          )}\}`;
        } else {
          // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
          text += `\$\{${item.variable}\}`;
        }
      }
      if (item.skill_variable !== undefined) {
        // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
        text += `\$\{${item.skill_variable}\}`;
      }
      if (item.system_variable !== undefined) {
        // eslint-disable-next-line no-useless-escape -- Reason: '$' is our syntax rather than js syntax.
        text += `\$\{${item.system_variable}\}`;
      }
    });
  }
  return text;
};

export const getResponseText = step => {
  const outputGeneric = _.get(step, 'output.generic');
  const textIndex = _.findIndex(outputGeneric, { response_type: 'text' });
  if (!outputGeneric || textIndex === -1) {
    return '';
  }
  const concat = _.get(
    step,
    `output.generic[${textIndex}].values[0].text_expression.concat`,
    null
  );
  if (!concat) {
    return _.get(step, `output.generic[${textIndex}].values[0].text`, null);
  }
  if (concat) {
    return textExpressionPiecesToText(concat);
  }

  return _.get(step, `output.generic[${textIndex}].values[0].text`, null);
};

export const getTitleFromResponseText = step => {
  if (step && step.title) {
    return step.title;
  }
  const outputGeneric = _.get(step, 'output.generic');
  const textIndex = _.findIndex(outputGeneric, { response_type: 'text' });
  if (!outputGeneric || textIndex === -1) {
    return 'No response';
  }
  const concat = _.get(
    step,
    `output.generic[${textIndex}].values[0].text_expression.concat`,
    null
  );
  if (!concat) {
    return _.get(
      step,
      `output.generic[${textIndex}].values[0].text`,
      'No response'
    );
  }
  let text = '';
  if (concat) {
    concat.forEach(item => {
      if (item.scalar) {
        text += item.scalar;
      }
      if (item.variable !== undefined) {
        text += '{variable}';
      }
      if (item.skill_variable !== undefined) {
        text += `{global_variable}`;
      }
    });
  }
  return text || 'No response';
};

export const removeVariables = text => {
  //replace ${step_xxx} with <span>{variable}</span>
  const output = text?.replace(variableNamesRegex, '{variable}');
  //if there is a trailing ${.. remove it
  //output = output.replace(truncatedVarRegex, '');
  return output;
};

export const isPatternsEntity = entity =>
  // `type` is a temporary attribute under entity which should not exist in the payload.
  entity?.type === 'patterns' ||
  Boolean(entity?.values?.some(v => v.type === 'patterns'));

export const getEntityType = (
  entityId: string,
  entitiesMap: Record<string, Entity> = {}
) => {
  const entityTypeIDsArray = Object.values(ENTITY_TYPES).map(
    entityTypeObj => entityTypeObj.id
  );
  if (entityTypeIDsArray.includes(entityId)) {
    return entityId;
  } else if (entityId?.length > 0) {
    if (isPatternsEntity(entitiesMap[entityId])) {
      return ENTITY_TYPES.regex.id;
    }
    return ENTITY_TYPES.option.id;
  }
};

export const getConditionCount = step => {
  return (
    _.get(step, 'condition.neq.length', 0) / 2 +
    _.get(step, 'condition.eq.length', 0) / 2 +
    _.get(step, 'condition.or.length', 0) +
    _.get(step, 'condition.and.length', 0)
  );
};
/**
 * Split a long text response (>= 400chars) into
 * an array of scalars
 * @param {string} text response text string
 *
 * @returns {{scalar: string}[]} an array of text scalars
 */
const splitLongTextIntoScalars = text => {
  const limit = 400;
  if (text.length < limit) {
    return [{ scalar: text }];
  }
  const delimiters = [',', '.', '!', '-', '，', '。', '！', '、'];
  const space = ' ';
  let index = limit - 1;
  while (index > 0) {
    const char = text.charAt(index);
    if (delimiters.includes(char)) {
      break;
    }
    index--;
  }
  // if we don't find any delimiter in the string [0, 400)
  // use space as a delimiter
  if (index === 0) {
    index = text.substring(0, limit).lastIndexOf(space);
  }
  // if we don't find any delimiter or space in the string [0, 400)
  // split the string at the limit
  if (index === 0) {
    index = limit - 1;
  }
  const shortText = text.substring(0, index + 1);
  return [
    { scalar: shortText },
    ...splitLongTextIntoScalars(text.substring(index + 1))
  ];
};

export const splitTextIntoExpressionFormat = (
  output,
  globalVars = [],
  systemVars = [],
  defaultValue = null,
  variablesList,
  variablesById = {}
) => {
  const splitter = /\$\{((\w+)\.?(([\w\[\]]+\.?)*))\}/g;
  const matches = output.matchAll(splitter);
  const concat = [];
  let lastIndex = 0;

  for (const match of matches) {
    const text = output.substring(lastIndex, match.index);
    lastIndex = match.index + match[0].length;
    concat.push(...splitLongTextIntoScalars(text));
    const mainVariableId = match[2];
    const path = match[3];
    if (globalVars.indexOf(mainVariableId) !== -1) {
      const skillVariableObj = { skill_variable: mainVariableId };
      if (defaultValue && mainVariableId === defaultValue.id) {
        skillVariableObj['default_value'] = { scalar: defaultValue.input }; // if we have just added a default_value, add it here
      } else if (variablesList[mainVariableId]?.default_value) {
        // there is an existing default_value
        skillVariableObj['default_value'] =
          variablesList[mainVariableId].default_value;
      }
      concat.push(skillVariableObj);
    } else if (systemVars.indexOf(mainVariableId) !== -1) {
      concat.push({ system_variable: mainVariableId });
    } else if (mainVariableId && path) {
      // we cannot use util to build the block because we don't have all variables information
      const mainVarObject = variablesById[mainVariableId];
      if (
        isSubactionVariable(mainVarObject) &&
        !mainVarObject.convoSkillVariable
      ) {
        // sub action variables maintain referential integrity, as a result they should use
        //'child_property' as opposed to 'variable_path'.
        //All non sub action variables must use 'variable_path'
        concat.push({
          variable: mainVariableId,
          child_property: path === 'null' ? null : path
        });
      } else {
        concat.push({
          variable: mainVariableId,
          variable_path: path === 'null' ? null : path
        });
      }
    } else if (variablesById[mainVariableId]) {
      concat.push({ variable: mainVariableId });
    } else {
      concat.push({ variable: null });
    }
  }
  if (lastIndex < output.length) {
    concat.push(
      ...splitLongTextIntoScalars(output.substring(lastIndex, output.length))
    );
  }
  return concat;
};

export const countErrorsInText = concatArray => {
  let count = 0;
  if (concatArray) {
    concatArray.forEach(item => {
      if (item.variable === null) {
        count++;
      } else if (item.child_property === null) {
        count++;
      }
      if (item.skill_variable === null) {
        count++;
      }
    });
  }
  return count;
};

export const applyTransformationRuleOnCondition = (
  condition,
  transformationRule,
  level = 0
) => {
  let transformationApplied = false;
  if (_.isPlainObject(condition) || _.isArray(condition)) {
    const newCondition = _.isPlainObject(condition)
      ? { ...condition }
      : [...condition];
    for (const key in condition) {
      const [newKey, transformedCondition] = transformationRule(
        key,
        newCondition[key],
        level
      );
      if (transformedCondition !== newCondition[key] || key !== newKey) {
        transformationApplied = true;
      }
      delete newCondition[key];
      newCondition[newKey] = transformedCondition;

      const transformedInnerCondition = applyTransformationRuleOnCondition(
        newCondition[newKey],
        transformationRule,
        level + 1
      );

      if (transformedInnerCondition !== newCondition[newKey]) {
        transformationApplied = true;
      }

      newCondition[newKey] = transformedInnerCondition;
    }

    return transformationApplied ? newCondition : condition;
  }

  return condition;
};

export const countErrorsInInvokeActionResolver = resolver => {
  let variable = 0;
  if (
    resolver &&
    resolver[RESOLVER_PROPERTIES.INVOKE_ACTION] &&
    resolver[RESOLVER_PROPERTIES.INVOKE_ACTION].parameters
  ) {
    for (const parameter of resolver[RESOLVER_PROPERTIES.INVOKE_ACTION]
      .parameters) {
      if (parameter.parameter === null) {
        variable++;
      }
      if (parameter?.value && parameter?.value?.variable === null) {
        variable++;
      } else if (
        parameter?.value &&
        parameter?.value?.skill_variable === null
      ) {
        variable++;
      }
      if (parameter?.value && parameter?.value?.child_property === null) {
        variable++;
      }
    }
  }
  let sub_action = 0;
  if (
    _.get(resolver, `${RESOLVER_PROPERTIES.INVOKE_ACTION}.action`) === null &&
    _.get(resolver, `${RESOLVER_PROPERTIES.INVOKE_ACTION}.policy`) !==
      RESOLVER_POLICY_DYNAMIC
  ) {
    sub_action++;
  }
  return { sub_action, variable };
};

export const countErrorsInCalloutResolver = resolver => {
  let variable = 0;
  let callout = 0;
  const requestMapping =
    resolver?.[RESOLVER_PROPERTIES.CALLOUT]?.request_mapping;
  const catalogItemId =
    resolver?.[RESOLVER_PROPERTIES.CALLOUT]?.internal?.catalog_item_id;
  const specHashId =
    resolver?.[RESOLVER_PROPERTIES.CALLOUT]?.internal?.spec_hash_id;

  if (catalogItemId === null || specHashId === null) {
    callout++;
  }

  if (requestMapping) {
    let paramsArray = [];
    Object.keys(requestMapping).forEach(reqKey => {
      paramsArray = paramsArray.concat(requestMapping[reqKey]);
    });
    for (const parameter of paramsArray) {
      if (parameter?.value && parameter?.value?.variable === null) {
        variable++;
      } else if (
        parameter?.value &&
        parameter?.value?.skill_variable === null
      ) {
        variable++;
      }
      if (parameter?.value && parameter?.value?.child_property === null) {
        variable++;
      }
    }
  }

  return { callout, variable };
};

export const countErrorsInClientActionsResolver = resolver => {
  let variable = 0;
  if (resolver?.client_actions) {
    resolver.client_actions.forEach(clientAction => {
      if (clientAction.parameters) {
        clientAction.parameters.forEach(parameter => {
          if (parameter.value) {
            if (parameter.value.variable === null) {
              variable++;
            } else if (parameter.value.skill_variable === null) {
              variable++;
            } else if (parameter.value.child_property === null) {
              variable++;
            }
          }
        });
      }
    });
  }
  return variable;
};

export const countErrorsInContext = context => {
  let count = 0;
  if (context && context.variables) {
    context.variables.forEach(assignment => {
      if (assignment.variable === null) {
        count++;
      }
      if (assignment.skill_variable === null) {
        count++;
      }
      if (assignment.value) {
        if (assignment.value.variable === null) {
          count++;
        }
        if (assignment.value.skill_variable === null) {
          count++;
        }
      }
    });
  }
  return count;
};

export const countErrorsInCondition = condition => {
  const count = { entity: 0, value: 0, variable: 0, child_property: 0 };
  if (
    typeof condition === 'string' ||
    typeof condition === 'number' ||
    typeof condition === 'boolean'
  ) {
    return count;
  }
  if (Array.isArray(condition)) {
    for (let i = 0; i < condition.length; i++) {
      const result = countErrorsInCondition(condition[i]);
      count.entity += result.entity;
      count.value += result.value;
      count.variable += result.variable;
      count.child_property += result.child_property;
    }
    return count;
  }
  if (typeof condition === 'object') {
    const keys = Object.keys(condition);
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === 'variable' || keys[i] === 'skill_variable') {
        if (condition[keys[i]] === null) {
          count.variable++;
        }
      } else if (keys[i] === 'child_property') {
        if (condition[keys[i]] === null) {
          count.child_property++;
        }
      } else if (keys[i] === 'from_entity') {
        if (condition[keys[i]] === null) {
          count.entity++;
        }
      } else if (keys[i] === 'value') {
        if (condition[keys[i]] === null) {
          count.value++;
        }
      } else {
        const result = countErrorsInCondition(condition[keys[i]]);
        count.entity += result.entity;
        count.value += result.value;
        count.variable += result.variable;
        count.child_property += result.child_property;
      }
    }
  }
  return count;
};

const conditionRefersToVariable = (condition, variableId) => {
  if (
    !condition ||
    typeof condition === 'string' ||
    typeof condition === 'number' ||
    typeof condition === 'boolean'
  ) {
    return false;
  }
  if (Array.isArray(condition)) {
    for (let i = 0; i < condition.length; i++) {
      if (conditionRefersToVariable(condition[i], variableId)) {
        return true;
      }
    }
  }
  if (condition.variable) {
    return condition.variable === variableId;
  }
  const keys = Object.keys(condition);
  return keys.some(key => {
    return conditionRefersToVariable(condition[key], variableId);
  });
};

export const stepRefersToStep = (
  step,
  currentStepId,
  currentVariableId,
  resultVariableId?
) => {
  if (currentStepId && _.get(step, 'resolver.step', null) === currentStepId) {
    return true;
  }
  if (_.get(step, `resolver.${RESOLVER_PROPERTIES.INVOKE_ACTION}`)) {
    const parameters = _.get(
      step,
      `resolver.${RESOLVER_PROPERTIES.INVOKE_ACTION}.parameters`
    );
    if (parameters) {
      for (const param of parameters) {
        if (param.value && param.value.variable === currentVariableId) {
          return true;
        }
      }
    }
  }
  if (_.get(step, 'resolver.type') === ResolverType.REPLAY) {
    const variables = _.get(step, `resolver.clear`);
    if (variables) {
      if (variables.some(variable => variable.variable === currentVariableId)) {
        return true;
      }
    }
  }
  if (_.get(step, 'resolver.type') === ResolverType.CALL_AN_EXTENSION) {
    const requestMap = _.get(step, 'resolver.callout.request_mapping', {});
    let parameters = [];
    if (!_.isEmpty(requestMap)) {
      const reqProps = Object.keys(requestMap);
      reqProps.forEach(prop => {
        parameters = parameters.concat(requestMap[prop]);
      });
    }
    if (
      parameters &&
      parameters.some(param => {
        const variableId = getVariableIdFromVariableValue(param.value);
        return variableId === currentVariableId;
      })
    ) {
      return true;
    }
  }
  if (_.get(step, 'resolver.type') === ResolverType.CLIENT_ACTIONS) {
    const { client_actions: clientActions } = step.resolver;
    if (clientActions) {
      const hasReference = clientActions.some(clientAction => {
        return clientAction.parameters?.some(parameter => {
          const variableId = getVariableIdFromVariableValue(parameter.value);
          return variableId === currentVariableId;
        });
      });
      if (hasReference) {
        return true;
      }
    }
  }
  if (step.condition) {
    if (
      conditionRefersToVariable(step.condition, currentVariableId) ||
      conditionRefersToVariable(step.condition, resultVariableId)
    ) {
      return true;
    }
  }
  if (step.context) {
    if (
      conditionRefersToVariable(step.context, currentVariableId) ||
      conditionRefersToVariable(step.context, resultVariableId)
    ) {
      return true;
    }
  }
  if (step.output && step.output.generic) {
    const textResponse = _.find(step.output.generic, { response_type: 'text' });
    if (textResponse && textResponse.values) {
      const value = _.find(textResponse.values, 'text_expression');
      if (value && value.text_expression && value.text_expression.concat) {
        if (
          value.text_expression.concat.some(
            segment =>
              segment.variable &&
              (segment.variable === currentVariableId ||
                segment.variable === resultVariableId)
          )
        ) {
          return true;
        }
      }
    }
  }
};

export const invalidateConditionOnDelete = (
  condition,
  variableId,
  entityId,
  invalidReferences
) => {
  let errorFound = false;
  if (condition) {
    if (
      typeof condition === 'string' ||
      typeof condition === 'number' ||
      typeof condition === 'boolean'
    ) {
      return errorFound;
    }
    if (Array.isArray(condition)) {
      for (let i = 0; i < condition.length; i++) {
        errorFound =
          invalidateConditionOnDelete(
            condition[i],
            variableId,
            entityId,
            invalidReferences
          ) || errorFound;
      }
    } else {
      const keys = Object.keys(condition);
      for (let i = 0; i < keys.length; i++) {
        if (keys[i] === 'variable') {
          if (variableId && condition[keys[i]] === variableId) {
            condition[keys[i]] = null; //null out and return true;
            invalidReferences.variable++;
            errorFound = true;
            continue;
          }
        }
        if (entityId && keys[i] === 'from_entity') {
          if (condition[keys[i]] === entityId) {
            if (condition.value) {
              condition.value = null;
              invalidReferences.value++;
            }
            condition[keys[i]] = null;
            errorFound = true;
            invalidReferences.entity++;
          }
        } else {
          errorFound =
            invalidateConditionOnDelete(
              condition[keys[i]],
              variableId,
              entityId,
              invalidReferences
            ) || errorFound;
        }
      }
    }
  }
  return errorFound;
};

export const updateEntityInCondition = (condition, entityId, newEntityId) => {
  if (
    !condition ||
    typeof condition === 'string' ||
    typeof condition === 'number' ||
    typeof condition === 'boolean'
  ) {
    return condition;
  }
  const newCondition = _.cloneDeep(condition);

  if (Array.isArray(newCondition)) {
    for (let i = 0; i < newCondition.length; i++) {
      newCondition[i] = updateEntityInCondition(
        newCondition[i],
        entityId,
        newEntityId
      );
    }
  } else {
    const keys = Object.keys(newCondition);
    for (let i = 0; i < keys.length; i++) {
      if (
        entityId &&
        keys[i] === 'from_entity' &&
        newCondition['from_entity'] === entityId
      ) {
        newCondition['from_entity'] = newEntityId;
      } else {
        newCondition[keys[i]] = updateEntityInCondition(
          newCondition[keys[i]],
          entityId,
          newEntityId
        );
      }
    }
  }
  return newCondition;
};

export const isStepInvalid = (step: Step) => {
  if (step) {
    if (step.invalid_references) {
      //Once actions are updated to correctly support invalid extension references
      return Object.values(step.invalid_references).some(
        reference => (reference as number) > 0
      );
    }
  }
  return false;
};

export const assembleEntityPayload = (operation, entityId, entityProps) => {
  return {
    operation,
    response: {
      entities: {
        entities: {
          [entityId]: entityProps
        }
      }
    }
  };
};

export const isSystemEntity = entityId => {
  return entityId && entityId.startsWith('sys-');
};

export const generateStepId = () => {
  // Generate a random stepId between 100 and 999
  return `step_${Math.round(Math.random() * (999 - 100) + 100)}`;
};

export const generateActionId = () => {
  // Generate a random actionId between 1000 and 50000
  return `action_${Math.round(Math.random() * (50000 - 1000) + 1000)}`;
};

export const generateSlotId = () => {
  // Generate a random slotId between 1000 and 50000
  return `slot_${Math.round(Math.random() * (50000 - 1000) + 1000)}`;
};

export const generateCollectionId = () => {
  // Generate a random collectionId between 1000 and 50000
  return `collection_${Math.round(Math.random() * (50000 - 1000) + 1000)}`;
};

const colorList = ['cyan', 'purple', 'green', 'teal'];

export const optionColorList = [2, 1, 3].reduce((accu, value) => {
  accu.push(...colorList.map(color => color + `-${value}0-${value + 5}0`));
  return accu;
}, []);

export class OptionColorMap {
  map = {};
  size = 0;
  constructor() {
    this.map = {};
    this.size = 0;
  }
  pickColor(name) {
    if (this.map[name]) {
      return this.map[name];
    }
    this.map[name] = optionColorList[this.size % optionColorList.length];
    this.size += 1;
    return this.map[name];
  }

  reset() {
    this.size = 0;
    this.map = {};
  }
}

export const getDefaultErrorResponseByEntityType = (
  entityType,
  language = 'en',
  options = { hasOptionResponse: true }
) => {
  if (!entityType || !language) {
    return '';
  }
  switch (entityType) {
    case ENTITY_TYPES.free_text.id:
    case ENTITY_TYPES.option.id:
    case ENTITY_TYPES.dynamic_option.id:
      if (options.hasOptionResponse) {
        return errorResponseStrings[language].OPTIONS_ERROR_RESPONSE;
      } else {
        return errorResponseStrings[language].ERROR_RESPONSE;
      }
    case ENTITY_TYPES.number.id:
      return errorResponseStrings[language].NUMBER_ERROR_RESPONSE;
    default:
      return errorResponseStrings[language]?.ERROR_RESPONSE;
  }
};

export const isDisambiguationEnabled = action => {
  const disambiguationOptOut = _.get(action, 'disambiguation_opt_out', true); // if opt_out is true, then disambiguation is not enabled
  return !disambiguationOptOut; // if opt_out true, enabled is false. if opt_out false, enabled true
};

export const isTopicSwitchEnabled = action => {
  return (
    !action?.topic_switch ||
    (action?.topic_switch?.allowed_from && action?.topic_switch?.allowed_into)
  );
};

export const isNeverReturnEnabled = action => {
  return Boolean(action?.topic_switch?.never_return);
};

const OptionColorMapInstance = new OptionColorMap();
export { OptionColorMapInstance };

export const getCustomCode = step => {
  const customCode = _.omit(_.get(step, 'output'), ['generic']);
  if (Object.keys(customCode).length === 0) {
    return undefined;
  }
  return customCode;
};

export const isMediaResponse = response_type => {
  return MEDIA_RESPONSES.includes(response_type);
};

export const isResponseTypeSupportedInActions = response_type => {
  return response_type === 'option'
    ? false
    : Object.values(RESPONSE_TYPES).includes(response_type);
};

export const getCustomResponseTypes = (step, entityValues) => {
  const outputGeneric = _.get(step, 'output.generic', []);
  const customResponseTypes: GenericResponse[] = _.filter(
    outputGeneric,
    response => {
      if (isResponseTypeSupportedInActions(response.response_type)) {
        return false;
      }
      return true;
    }
  );

  if (step?.question?.entity === ENTITY_TYPES.date.id) {
    const dateResponseTypes = outputGeneric.filter(
      o => o.response_type === RESPONSE_TYPES.date
    );
    if (dateResponseTypes.length !== 1) {
      customResponseTypes.push({
        response_type: RESPONSE_TYPES.date
      } as DateResponse);
    }
  }

  if (customResponseTypes.length === 0) {
    return [];
  }
  // after we filtered out the data type response, all rest of responses should be customized responses.
  if (step.question?.data_type) {
    return customResponseTypes;
  }
  if (!Array.isArray(entityValues) || entityValues.length === 0) {
    // The step has no options associated with entity
    // any response type in the output.generic other than text is a custom response type
    return customResponseTypes;
  } else {
    // The step has an options response associated with entity
    let hasOptionsAssociatedWithEntities = true;
    customResponseTypes.forEach(customResponse => {
      const options = (customResponse as OptionResponse).options;
      if (Array.isArray(options) && options.length === entityValues.length) {
        for (const option of options) {
          const label = _.get(option, 'label');
          const value = _.get(option, 'value.input.text');
          if (!label || !value || label.toLowerCase() !== value.toLowerCase()) {
            hasOptionsAssociatedWithEntities = false;
            break;
          }
          if (
            !entityValues.some(
              v => v?.value?.toLowerCase() === label?.toLowerCase()
            )
          ) {
            //the current options label does not match any of the entity values
            hasOptionsAssociatedWithEntities = false;
            break;
          }
        }
      } else {
        hasOptionsAssociatedWithEntities = false;
      }
    });
    if (
      customResponseTypes.length === 1 &&
      (hasOptionsAssociatedWithEntities ||
        customResponseTypes[0].response_type ===
          RESPONSE_TYPES.response_from_data_type)
    ) {
      return [];
    }
    return customResponseTypes;
  }
};

export const isDateTimeConditionVariable = type =>
  type === ENTITY_TYPES.date.id ||
  type === ENTITY_TYPES.time.id ||
  type === ENTITY_TYPES.date_time.id;

export const hasDateResponseType = output => {
  return !!output?.generic?.some(
    response => response.response_type === RESPONSE_TYPES.date
  );
};

export const computeEntityFromVariable = variableId => {
  switch (variableId) {
    case 'no_action_matches_count':
      return 'sys-number';
    case 'fallback_reason':
      return 'sys-synthetic_fallback_reason_entity';
    case 'channel_name':
      return 'sys-synthetic_channel_name_entity';
    case 'last_seen_at':
      return 'sys-synthetic_last_seen_at_entity';
    default:
      return undefined;
  }
};

export const computeTypeFromVariable = variableId => {
  switch (variableId) {
    case 'no_action_matches_count':
    case 'fallback_reason':
      return 'entity';
    case 'current_date':
      return 'sys-date';
    case 'current_time':
      return 'sys-time';
    case 'now':
      return 'sys-date-time';
    default:
      return undefined;
  }
};

export const getStepIdFromHash = () => {
  const { hash } = window.location;
  const targetStepId = hash?.replace('#', '');
  return targetStepId.length > 0 ? targetStepId : null;
};

export const getNumericRepresentation = value => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) {
    return value;
  } // return original string if it is not a number
  return num;
};

export const getExistingInitialvalue = (variables, existingVariableId) => {
  const variable = variables.find(
    variable => variable.variable === existingVariableId
  );
  return (
    variable?.initial_value?.scalar ||
    variable?.initial_value?.time?.value ||
    variable?.initial_value?.expression
  );
};

export const getActionTypeSegment = actionId => {
  return isTour(actionId)
    ? 'tours'
    : isDefaultAction(actionId)
    ? 'system'
    : 'custom';
};

/**
 * Generate the url of an action in the action editor in the tooling.
 * @param {string} baseUrl The base url of the target
 * @param {string} actionId the target action id
 * @param {string} stepId the target step id. This can be a hash `#step_123`, we will use it directly if it is a hash
 * @returns {string} the target action url in the tooling
 */
export const generateTargetActionUrl = (baseUrl, actionId, stepId) => {
  let stepIdSegment = stepId;
  if (stepId && !stepId?.startsWith('#')) {
    stepIdSegment = `#${stepId}`;
  }
  return urlJoin(
    baseUrl,
    'actions',
    getActionTypeSegment(actionId),
    'edit',
    actionId,
    stepIdSegment
  );
};

/**
 * Generate the url of the convoSkill action editor in the tooling.
 * @param {string} baseUrl The base url of the target
 * @param {string} actionId the target action id
 * @returns {string} the target action url in the tooling
 */
export const generateConvoSkillActionEditUrl = (
  baseUrl: string,
  actionId: string
): string => {
  return urlJoin(baseUrl, 'actions/custom/convoskill/edit', actionId);
};

export const generateTargetAIGuidedActionUrl = (baseUrl, actionId, stepId) => {
  let stepIdSegment = stepId;
  if (stepId && !stepId?.startsWith('#')) {
    stepIdSegment = `#${stepId}`;
  }
  return urlJoin(
    baseUrl,
    'actions',
    'custom',
    'ai-guided',
    'edit',
    actionId,
    stepIdSegment
  );
};

export const generateTargetCollectionUrl = (baseUrl, collection) => {
  // NOTE on update to react-router v6, the incoming baseUrl was captured at a higher level of
  //   the dom and ends with a single `/actions` so need to add another to get `actions/actions/custom..`
  return urlJoin(baseUrl, 'actions', 'custom', 'collection', collection);
};
/**
 * Sort the steps based on `next_step` property.
 * @param {Array} steps a step list
 * @returns {Array}
 */
export const sortSteps = (steps: Step[]) => {
  if (!steps || steps.length === 0) {
    return steps;
  }
  let i = 0;
  let finalStep = steps[i];

  while (finalStep && finalStep.next_step && i < steps.length) {
    //find the step with no next_step prop
    i++;
    finalStep = steps[i];
  }

  if (finalStep && !finalStep.next_step) {
    //this is the 'final step', now find the step that references this step
    //that will be the second last step, and so on
    const lastToFirst = [];
    lastToFirst.push(finalStep.step);
    let prevStep = _.find(steps, { next_step: finalStep.step });
    while (prevStep) {
      lastToFirst.push(prevStep.step);
      prevStep = _.find(steps, { next_step: prevStep.step });
    }
    //we found the steps in reverse order.. now reverse the list to get the list
    //from first to last.
    const order = _.reverse(lastToFirst);
    const orderedSteps: Step[] = [];
    order.forEach(item => {
      const found = _.find(steps, { step: item });
      if (found) {
        orderedSteps.push({ ...found });
      }
    });
    return orderedSteps;
  }
  return steps;
};

export const generateUniqueEntityName = (
  defaultEntityName,
  existingEntityNames
) => {
  let generatedEntityName = defaultEntityName;
  let index = 1;

  while (!existingEntityNames.contains(generatedEntityName)) {
    generatedEntityName = `${defaultEntityName}_${index++}`;
  }

  return generatedEntityName;
};

export const getSyntheticEntities = (
  triggerWordsPresent,
  digressionEnabled
) => {
  const fallbackReasons = [
    { value: 'Agent requested', type: 'synonyms' },
    { value: 'No matches', type: 'synonyms' },
    { value: 'Step validation failed', type: 'synonyms' }
  ];
  if (triggerWordsPresent) {
    fallbackReasons.push(
      { value: 'Danger word detected', type: 'synonyms' },
      { value: 'Profanity detected', type: 'synonyms' }
    );
  }
  if (digressionEnabled) {
    fallbackReasons.push({
      value: 'Failed to confirm topic return',
      type: 'synonyms'
    });
  }
  return [
    {
      entity: 'sys-synthetic_fallback_reason_entity', //sys-* is a reserved name so we can be sure there won't be a real entity with this id
      values: fallbackReasons
    },
    {
      entity: 'sys-synthetic_channel_name_entity',
      values: [
        { value: 'Web chat', type: 'synonyms' },
        { value: 'Phone', type: 'synonyms' },
        { value: 'SMS', type: 'synonyms' },
        { value: 'Facebook Messenger', type: 'synonyms' },
        { value: 'Microsoft Teams', type: 'synonyms' },
        { value: 'Slack', type: 'synonyms' },
        { value: 'Whatsapp', type: 'synonyms' }
      ]
    }
  ];
};

export const VAR_REGEX =
  /\${.+}|((((\.)|(new\s+))\w+)\s*\()|(\d+\s*(\+|\*|\\|-)\s*\d+)|(\{.+:.+\}|\{\s*\}|\[\s*\]|^\[.*\]$)|(output\.\w+)|(input\.\w+)/;

export const TYPE_CATEGORIES = {
  BOOLEAN: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.BOOLEAN,
    children: null
  },
  CURRENCY: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.CURRENCY,
    children: null
  },
  DATE: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.DATE,
    children: null
  },
  EXPRESSION: {
    category: CUSTOM_CATEGORY,
    categoryId: 'expression',
    children: null
  },
  FREE_TEXT: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.STRING,
    children: null
  },
  NUMBER: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.NUMBER,
    children: null
  },
  OPTION: {
    category: CUSTOM_CATEGORY,
    categoryId: ENTITY_TYPES.option.id,
    children: null
  },
  PERCENTAGE: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.PERCENTAGE,
    children: null
  },
  TIME: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.TIME,
    children: null
  },
  YES_NO: {
    category: CUSTOM_CATEGORY,
    categoryId: DATA_TYPE.YES_NO,
    children: null
  }
};

export const createCategoriesByType = (
  addExpressionBlock,
  varType,
  customDataType,
  customEntity
) => {
  const addedCategories = [];

  const type = isSystemEntity(varType) ? getDataTypeByEntity(varType) : varType;

  if (type === DATA_TYPE.BOOLEAN) {
    addedCategories?.push(TYPE_CATEGORIES.BOOLEAN);
  } else if (type === DATA_TYPE.CURRENCY) {
    addedCategories?.push(TYPE_CATEGORIES.CURRENCY);
  } else if (type === DATA_TYPE.DATE) {
    addedCategories?.push(TYPE_CATEGORIES.DATE);
  } else if (type === DATA_TYPE.NUMBER) {
    addedCategories?.push(TYPE_CATEGORIES.NUMBER);
  } else if (customDataType) {
    // More custom data types can be added here
    if (customDataType?.response_type === ENTITY_TYPES.option.id) {
      addedCategories?.push({
        ...TYPE_CATEGORIES.OPTION,
        children: customDataType?.response?.options?.map(option => ({
          from_entity: customDataType?.entity,
          title: option.label
        }))
      });
    }
  } else if (customEntity) {
    addedCategories?.push({
      ...TYPE_CATEGORIES.OPTION,
      children: customEntity?.values?.map(value => ({
        from_entity: customEntity?.entity,
        title: value?.value
      }))
    });
  } else if (type === DATA_TYPE.PERCENTAGE) {
    addedCategories?.push(TYPE_CATEGORIES.PERCENTAGE);
  } else if (type === DATA_TYPE.STRING) {
    addedCategories?.push(TYPE_CATEGORIES.FREE_TEXT);
  } else if (type === DATA_TYPE.TIME) {
    addedCategories?.push(TYPE_CATEGORIES.TIME);
  } else if (type === DATA_TYPE.YES_NO) {
    addedCategories?.push(TYPE_CATEGORIES.YES_NO);
  }

  if (addExpressionBlock) {
    addedCategories.push(TYPE_CATEGORIES.EXPRESSION);
  }

  return addedCategories;
};

export const filterVariableCategoriesList = (
  type,
  variableCategoryList,
  variableId?
) => {
  if (
    type === DATA_TYPE.ANY ||
    type === DATA_TYPE.OBJECT ||
    type === undefined
  ) {
    return variableCategoryList;
  }

  type = isSystemEntity(type) ? getDataTypeByEntity(type) : type;

  const filteredCategoryList = [];
  variableCategoryList.forEach(variableCategory => {
    const filteredChildren = variableCategory?.children.filter(child => {
      if (variableId === child?.variable) {
        return false;
      }

      let childType = child?.data_type || child?.type;
      childType = isSystemEntity(childType)
        ? getDataTypeByEntity(childType)
        : childType;

      if (type === childType) {
        return true;
      }
      if (childType === DATA_TYPE.ANY) {
        return true;
      }

      // Number type should be compatible with all numeric types
      if (type === DATA_TYPE.NUMBER && isNumericDataType(childType)) {
        return true;
      }

      // String type should be compatible with option and regex types
      if (
        type === DATA_TYPE.STRING &&
        (childType?.startsWith('data-type') ||
          childType?.startsWith('entity')) &&
        variableCategory.categoryId !==
          VARIABLE_CATEGORIES.OPEN_API_EXTENSION &&
        !isSystemEntity(child.type)
      ) {
        return true;
      }

      return false;
    });

    if (!_.isEmpty(filteredChildren)) {
      filteredCategoryList.push({
        category: variableCategory?.category,
        categoryId: variableCategory?.categoryId,
        children: filteredChildren,
        stepDefinedIndex: variableCategory?.stepDefinedIndex
      });
    }
  });

  return filteredCategoryList;
};

/**
 * Get the variable id from a block which contians some kind of variable id under different properties.
 * If `availableVariablesMap` is not provided, then it will not validate the subaction variable
 * @param {Object} block an object contains some kind of variable id
 * @param {Object} availableVariablesMap all available variables map
 * @returns {string | null} the target variable id
 */
export const getVariableIdFromBlock = (block, availableVariablesMap = null) => {
  let targetId = null;
  if (block) {
    if (
      block.child_property === null ||
      block.variable === null ||
      block.variable_path === null
    ) {
      return targetId;
    }
    const subPath = (block.child_property || block.variable_path) ?? null;
    if (subPath) {
      targetId = generateComplexVariableId(block.variable, subPath);
      if (availableVariablesMap && !availableVariablesMap[targetId]) {
        targetId = block.variable;
      }
    } else if (block.skill_variable) {
      targetId = block.skill_variable;
    } else if (block.system_variable) {
      targetId = block.system_variable;
    } else {
      targetId = block.variable;
    }
  }
  return targetId;
};

export const getYesNoTranslationForLang = (language = 'en') => {
  // language can be 'xx-xx' (a dialect) or 'xx'
  let translation = yesNoTranslation[language];
  // if dialect is not found, find translation for 'xx'
  if (!translation) {
    //get first 2 letters of the language.
    const lang = language.slice(0, 2);
    translation = yesNoTranslation[lang];
  }
  // if translation still not found, default to english
  if (!translation) {
    //add this check in case client passes in language that has no translations - default to en
    return yesNoTranslation['en'];
  }
  return translation;
};

export const getValidEntityIdFromSavedResponseTitle = title => {
  return title.replace(' (', '_').replace(')', '_');
};

/**
 * Get the string representation of a collection in the condition
 * @param {Array} collection a collection of values
 * @param {Object?} variableObj the variable object in the condition
 * @returns {string} A string showing all values
 */
export const getCollectionString = (
  collection,
  variableObj: { subtype?: string } = {}
) => {
  if (Array.isArray(collection)) {
    return collection
      .map(item => {
        if (_.has(item, 'scalar')) {
          if (variableObj.subtype === 'day_of_the_week') {
            return DAYS_OF_THE_WEEK.find(f => f.scalar === item.scalar)?.text;
          }
          return item.scalar;
        }
        if (_.has(item, 'value')) {
          return item.value;
        }
        return _.toString(item);
      })
      .join(', ');
  }
  return _.toString(collection);
};

export const isMultipleGroups = condition => {
  const groupsOperator = Object.keys(condition)[0];
  const firstBlock = condition[groupsOperator]?.[0];
  let returnVal = false;
  if (groupsOperator && firstBlock) {
    returnVal =
      (groupsOperator === 'and' || groupsOperator === 'or') &&
      (Object.keys(firstBlock)[0] === 'and' ||
        Object.keys(firstBlock)[0] === 'or');
  }
  return returnVal;
};

const isSingleGroup = condition => {
  const groupsOperator = Object.keys(condition)[0];
  const firstBlock = condition[groupsOperator]?.[0];
  let returnVal = false;
  if (groupsOperator && firstBlock) {
    returnVal =
      (groupsOperator === 'and' || groupsOperator === 'or') &&
      Object.keys(firstBlock)[0] !== 'and' &&
      Object.keys(firstBlock)[0] !== 'or';
  }
  return returnVal;
};

const isConditionBlock = condition =>
  typeof condition === 'object' &&
  Object.keys(condition)[0] !== 'and' &&
  Object.keys(condition)[0] !== 'or';

export const parseStepCondition = memoize(condition => {
  let conditionGroups = [];
  let groupsOperator = 'and';
  if (!!condition && !_.isEmpty(condition)) {
    if (isMultipleGroups(condition)) {
      groupsOperator = Object.keys(condition)[0];
      conditionGroups = condition[groupsOperator].map(group =>
        isConditionBlock(group) ? { and: [group] } : group
      );
    } else if (isSingleGroup(condition)) {
      conditionGroups = [condition];
    } else if (isConditionBlock(condition)) {
      conditionGroups = [{ and: [condition] }];
    }
  }
  return [conditionGroups, groupsOperator];
}, _.isEqual);

export const getConditionsCount = conditionGroups => {
  let count = 0;
  conditionGroups.forEach(group => {
    count += (Object.values(group)[0] as any)?.length;
  });
  return count;
};

export const getResponseCollectionBehavior = question => {
  if (question?.response_collection_behavior) {
    return question.response_collection_behavior;
  }
  if (question?.only_populate_when_prompted) {
    return ResponseCollectionBehavior.ALWAYS_ASK;
  }
  return ResponseCollectionBehavior.OPTIONALLY_ASK;
};

export const hasDynamicOptions = question => Boolean(question?.source_variable);

export const getAllowTopicSwitch = question => {
  if (question?.allow_topic_switch) {
    return question.allow_topic_switch;
  }
  return false;
};

export const isStepNeverAsk = (step: Step) => {
  return (
    step.question?.response_collection_behavior ===
    ResponseCollectionBehavior.NEVER_ASK
  );
};

//returns true if title is unique.
// If an actionId is specified, that action is exempt from uniqueness check
export const uniqueTitle = (
  arrayObject,
  title: string,
  currentTitle: string,
  actionId: string = null
) => {
  const foundArray = arrayObject.filter(
    o => o?.title?.toLowerCase() === title?.toLowerCase()
  );
  const foundLength = foundArray.length;
  if (
    foundLength === 1 &&
    (currentTitle === title || (actionId && actionId === foundArray[0].action))
  ) {
    return true;
  }
  if (foundLength < 1) {
    return true;
  } else {
    return false;
  }
};

/**
 * For both messages send from the Preview Page and Actions Preview we add custom properties to our segment events.
 */
export const calculateSegmentProperties = (
  intents: any[],
  entities: any[],
  responseTypes: any[],
  debug: any,
  actionId: string
) => {
  // Send segment information
  const messageTopIntentConfidence = _.get(intents, '[0].confidence', 0);
  const messageEntityCount = _.get(entities, 'length', 0);
  const messageResponseTypes = responseTypes;
  let messageConditionType = 'none';
  let messagePromptType = 'answer';
  const history = _.get(debug, 'turn_events', _.get(debug, 'history'));
  if (!debug || !history) {
    return {
      messageCategory: 'simple',
      messageDialogError: '', // NOTE: This data is not exposed by web chat so we assume these values
      messageResultCode: '200', // NOTE: This data is not exposed by web chat so we assume these values
      messageIsPrebuilt: isDefaultAction(actionId),
      messageTopIntentConfidence,
      messageResponseTypes,
      messageEntityCount,
      messageConditionType,
      messagePromptType
    };
  }
  let messageIsPrebuilt = false;
  let messageCategory = 'simple';
  const action = _.find(
    history,
    event =>
      event.event === TurnEventType.ACTION_VISITED &&
      event.condition_type !== 'user_defined'
  );
  if (action) {
    switch (action.condition_type) {
      case 'welcome':
      case 'anything_else':
      case 'fallback':
      case 'irrelevant': {
        messageIsPrebuilt = true;
        messageConditionType = action.condition_type;
        break;
      }
      default:
        break;
    }
  }
  if (
    _.find(history, { event: TurnEventType.STEP_VISITED, has_question: true })
  ) {
    messagePromptType = 'question';
    messageCategory = 'complex';
  }
  if (
    _.find(history, {
      condition_type: 'user_defined',
      event: TurnEventType.STEP_VISITED
    })
  ) {
    messageConditionType = 'user_defined';
    messageCategory = 'complex';
  }
  if (_.find(history, { event: TurnEventType.HANDLER_VISITED })) {
    messageCategory = 'complex';
  }
  if (
    _.find(
      history,
      event =>
        event.event === TurnEventType.STEP_ANSWERED && event.prompted !== true
    )
  ) {
    messageCategory = 'complex';
  }
  return {
    messageCategory, //simple/complex
    messagePromptType, //answer/question (question if prompting the user for input)
    messageIsPrebuilt,
    messageTopIntentConfidence,
    messageResponseTypes,
    messageEntityCount,
    messageConditionType,
    messageDialogError: '', // NOTE: This data is not exposed by web chat so we assume these values
    messageResultCode: '200' // NOTE: This data is not exposed by web chat so we assume these values
  };
};

// Supported response types for Phone preview in Actions
export const PhoneTTSPreviewSupportedResponseTypes: string[] = [
  RESPONSE_TYPES.text,
  RESPONSE_TYPES.option,
  RESPONSE_TYPES.search,
  RESPONSE_TYPES.suggestion
];
